import { BehaviorSubject } from 'rxjs';
import { ReactNode } from 'react';
import { FilterTypes } from '../../../../interfaces/src/lib/enums/index';

// Defining an interface to type-check the state related to the filter.
// This ensures that the filter state always has an `isOpen` boolean and `content` that can either be a React node or null.
interface FilterState {
  isOpen: boolean; // Whether the filter should be open or closed.
  content: ReactNode | null; // The content to be displayed in the filter, if any.
  smallScreenFilterLabel?: string;
  filterId?: string;
  filterType?: FilterTypes | '';
}

// Defining the initial state for the filter. By default, the filter is closed (`isOpen: false`) and has no content (`content: null`).
const initialFilterState: FilterState = {
  isOpen: false,
  content: null,
  smallScreenFilterLabel: '',
  filterType: '',
};

export type InnerFIltersContent = {
  id: string;
  type: string;
  content: ReactNode;
};

type MultiFilterContentState = {
  filtersRegistry: InnerFIltersContent[];
};
const InitialMultiFilterContentState: MultiFilterContentState = {
  filtersRegistry: [],
};

// Creating a BehaviorSubject to manage and emit the state of the filter.
// BehaviorSubject is used here because it will always provide the last emitted value to any new subscribers.
export const filterState$ = new BehaviorSubject<FilterState>(initialFilterState);

const multiFilterContentState = new BehaviorSubject<MultiFilterContentState>(
  InitialMultiFilterContentState
);
// A function to update the filter's state. It takes a FilterState object and uses the `next` method of BehaviorSubject
// to emit this new state to all subscribers. Effectively allows different parts of the application
// to update the state of the filter.
export const updateFilterState = (state: FilterState): void => {
  filterState$.next(state);
};

export const updateMultiFilterContentState = (newContent: InnerFIltersContent): void => {
  const currentState = multiFilterContentState.getValue();
  let updatedState: MultiFilterContentState;

  if (currentState.filtersRegistry.some(({ type }) => type === newContent.type)) {
    updatedState = {
      filtersRegistry: currentState.filtersRegistry.map(({ id, type, content }) =>
        type === newContent.type ? { ...newContent } : { id, type, content }
      ),
    };
  } else {
    updatedState = {
      filtersRegistry: [...currentState.filtersRegistry, newContent],
    };
  }

  multiFilterContentState.next(updatedState ?? currentState);
};

// Exposing the filterState as an observable.
export const filterState = filterState$.asObservable();
export const multiFilterContentState$ = multiFilterContentState.asObservable();
